/**
 * Phase 2A — Owner year-end view.
 *
 * Lists per-emp YTD totals + lets HR open a 50ทวิ slip for any
 * employee. The 50ทวิ card has a built-in print button that triggers
 * the browser's native PDF export — no server-side rendering yet.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import {
  useYearEndSummary,
  useFiftyTawi,
  useRecomputeYtd,
} from '../../hooks';
import { Printer, RotateCw, X, Download, FileDown, FolderDown } from 'lucide-react';
import { useAuthStore } from '../../lib/store';
import { apiDownload } from '../../lib/api-client';
import { useToast } from '../../components/ui/toast';
import { useConfirm } from '../../components/ui/confirm';

const API_BASE = import.meta.env.VITE_API_BASE ?? '';

function thaiYear(y: number): number {
  return y + 543;
}

function fmt(n: number): string {
  return new Intl.NumberFormat('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}

/** Content-only version used by the Wage hub. */
export function OwnerYearEndContent() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [selectedEmp, setSelectedEmp] = useState<string | null>(null);
  // key ของไฟล์ที่กำลังดาวน์โหลด (กันกดซ้ำระหว่าง render PDF ฝั่ง server)
  const [downloading, setDownloading] = useState<string | null>(null);
  const summary = useYearEndSummary(year);
  const recompute = useRecomputeYtd();
  const tawi = useFiftyTawi(year, selectedEmp);
  const toast = useToast();
  const confirm = useConfirm();

  async function handleDownload(key: string, path: string, filename: string) {
    if (downloading) return;
    setDownloading(key);
    try {
      await apiDownload(path, filename);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ดาวน์โหลดไม่สำเร็จ');
    } finally {
      setDownloading(null);
    }
  }

  async function handleRecompute() {
    if (!(await confirm({ title: 'คำนวณ YTD ใหม่', message: `จาก payroll ของปี ${year} (กรณีตัวเลขเพี้ยน)` }))) return;
    try {
      await recompute.mutateAsync(year);
      toast.success('คำนวณใหม่เรียบร้อย');
    } catch (e) { toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'); }
  }

  async function handlePnd1k() {
    // CSV endpoint returns a file directly — fetch with the JWT then
    // trigger a client-side download (can't use <a download> because
    // the endpoint needs the Authorization header).
    const jwt = useAuthStore.getState().jwt;
    try {
      const res = await fetch(`${API_BASE}/owner/year-end/pnd1k?year=${year}`, {
        headers: jwt ? { Authorization: `Bearer ${jwt}` } : undefined,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pnd1k-${year}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(`ดาวน์โหลดไม่สำเร็จ: ${e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'}`); }
  }

  return (
    <>
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <p className="text-sm text-gray-600">ปี {year} (พ.ศ. {thaiYear(year)})</p>
        <div className="flex items-center gap-1.5 shrink-0">
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="rounded-lg border border-gray-300 px-2 py-1 text-xs"
          >
            {[0, 1, 2, 3].map((i) => {
              const y = currentYear - i;
              return <option key={y} value={y}>{thaiYear(y)}</option>;
            })}
          </select>
          <button onClick={handleRecompute} disabled={recompute.isPending} className="btn-secondary btn-slim">
            <RotateCw className="w-3.5 h-3.5" /> คำนวณใหม่
          </button>
          <button onClick={handlePnd1k} className="btn-secondary btn-slim">
            <Download className="w-3.5 h-3.5" /> ภงด.1ก (CSV ตรวจทาน)
          </button>
          <button
            onClick={() => handleDownload('pnd1a-txt', `/owner/export/pnd1a?year=${year}&type=txt`, `pnd1a-${year}.txt`)}
            disabled={downloading !== null}
            className="btn-secondary btn-slim"
          >
            <FileDown className="w-3.5 h-3.5" /> {downloading === 'pnd1a-txt' ? 'กำลังสร้าง…' : 'ภ.ง.ด.1ก (ยื่น e-filing)'}
          </button>
          <button
            onClick={() => handleDownload('pnd1a-pdf', `/owner/export/pnd1a?year=${year}&type=pdf`, `pnd1a-${year}.pdf`)}
            disabled={downloading !== null}
            className="btn-secondary btn-slim"
          >
            <FileDown className="w-3.5 h-3.5" /> {downloading === 'pnd1a-pdf' ? 'กำลังสร้าง…' : 'ภ.ง.ด.1ก (PDF)'}
          </button>
          <button
            onClick={() => handleDownload('tawi-zip', `/owner/year-end/50tawi/zip?year=${year}`, `50tawi-${thaiYear(year)}.zip`)}
            disabled={downloading !== null}
            className="btn-secondary btn-slim"
          >
            <FolderDown className="w-3.5 h-3.5" /> {downloading === 'tawi-zip' ? 'กำลังสร้างทุกใบ…' : 'ดาวน์โหลด 50 ทวิ ทั้งหมด (zip)'}
          </button>
        </div>
      </div>
      {/* Totals strip */}
      {summary.data && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 mb-4">
          <Total label="รวม Gross"   value={summary.data.totals.gross_sum} />
          <Total label="ภาษีรวม"      value={summary.data.totals.tax_sum} accent="red" />
          <Total label="SSO รวม"      value={summary.data.totals.sso_sum} />
          <Total label="PF รวม"       value={summary.data.totals.pf_sum} />
          <Total label="เงินได้พึงประเมิน" value={summary.data.totals.taxable_sum} />
        </div>
      )}

      {/* Per-emp table */}
      {summary.isLoading ? (
        <p className="text-center text-gray-500 py-8">กำลังโหลด…</p>
      ) : !summary.data || summary.data.employees.length === 0 ? (
        <p className="text-center text-gray-500 py-8">ยังไม่มี YTD สำหรับปี {year} — ต้องล็อก payroll งวดในปีนี้ก่อน</p>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-3 py-2 font-medium">พนักงาน</th>
                <th className="text-right px-2 py-2">เดือน</th>
                <th className="text-right px-2 py-2">รายได้รวม</th>
                <th className="text-right px-2 py-2">หัก ภงด.</th>
                <th className="text-right px-2 py-2">สุทธิ</th>
                <th className="text-center px-2 py-2">50ทวิ</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {summary.data.employees.map((r) => (
                <tr key={r.emp_code}>
                  <td className="px-3 py-1.5">
                    <div className="text-xs font-medium">{r.emp_name}</div>
                    <div className="text-[10px] text-gray-400 font-mono">{r.emp_code}</div>
                  </td>
                  <td className="px-2 py-1.5 text-right text-xs">{r.months_locked}/12</td>
                  <td className="px-2 py-1.5 text-right">{fmt(r.gross_ytd)}</td>
                  <td className="px-2 py-1.5 text-right text-red-700">{fmt(r.tax_ytd)}</td>
                  <td className="px-2 py-1.5 text-right font-semibold">{fmt(r.net_ytd)}</td>
                  <td className="px-2 py-1.5 text-center whitespace-nowrap">
                    <button
                      onClick={() => setSelectedEmp(r.emp_code)}
                      className="text-xs text-brand-600 hover:underline"
                    >
                      เปิด
                    </button>
                    <span className="mx-1 text-gray-300">·</span>
                    <button
                      onClick={() =>
                        handleDownload(
                          `tawi-${r.emp_code}`,
                          `/owner/year-end/50tawi/pdf?year=${year}&emp=${encodeURIComponent(r.emp_code)}`,
                          `50tawi-${thaiYear(year)}-${r.emp_code}.pdf`,
                        )
                      }
                      disabled={downloading !== null}
                      className="text-xs text-brand-600 hover:underline disabled:text-gray-400"
                      title="PDF 50 ทวิ ฟอร์มทางการ"
                    >
                      {downloading === `tawi-${r.emp_code}` ? 'กำลังสร้าง…' : 'PDF 50 ทวิ'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 text-xs text-gray-500 text-center">
        <Link to="/factsheet#tax" className="underline hover:text-brand-700">วิธีคิดภาษี</Link>
        <span className="mx-2">·</span>
        <Link to="/factsheet#ytd" className="underline hover:text-brand-700">ยอด YTD + true-up ธ.ค.</Link>
        <span className="mx-2">·</span>
        <Link to="/factsheet" className="underline hover:text-brand-700">หลักการคำนวณทั้งหมด</Link>
      </div>

      {selectedEmp && tawi.data && (
        <FiftyTawiModal data={tawi.data} onClose={() => setSelectedEmp(null)} />
      )}
    </>
  );
}

/** Standalone page kept for legacy /owner/year-end (now redirects via Wage hub). */
export function OwnerYearEnd() {
  return (
    <AdminLayout title="สรุปปลายปี / 50 ทวิ">
      <OwnerYearEndContent />
    </AdminLayout>
  );
}

function Total({ label, value, accent }: { label: string; value: number; accent?: 'red' }) {
  return (
    <div className="card p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className={`font-semibold ${accent === 'red' ? 'text-red-700' : ''}`}>
        ฿ {fmt(value)}
      </div>
    </div>
  );
}

function FiftyTawiModal({
  data,
  onClose,
}: {
  data: ReturnType<typeof useFiftyTawi>['data'];
  onClose: () => void;
}) {
  if (!data) return null;
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white max-w-2xl w-full rounded-lg p-6 my-4 print:m-0 print:p-4 print:rounded-none print:shadow-none">
        <div className="flex justify-between items-center mb-4 print:hidden">
          <h2 className="font-bold">หนังสือรับรองการหักภาษี ณ ที่จ่าย</h2>
          <div className="flex gap-2">
            <button onClick={() => window.print()} className="btn-primary text-sm">
              <Printer className="w-4 h-4" /> พิมพ์ / Save PDF
            </button>
            <button onClick={onClose} className="text-gray-400"><X className="w-5 h-5" /></button>
          </div>
        </div>

        {data.no_data ? (
          <p className="text-gray-500 text-center py-8">ยังไม่มี YTD สำหรับพนักงานคนนี้ในปี {data.year}</p>
        ) : (
          <>
            <div className="text-center border-b pb-3 mb-4">
              <div className="text-lg font-bold">หนังสือรับรองการหักภาษี ณ ที่จ่าย (50 ทวิ)</div>
              <div className="text-sm">ปีภาษี {data.year} / พ.ศ. {thaiYear(data.year)}</div>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm mb-4">
              <div>
                <div className="text-xs text-gray-500 mb-1">ผู้จ่ายเงิน</div>
                <div className="font-medium">{data.tenant?.company_name ?? '-'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">เลขผู้เสียภาษี</div>
                <div className="font-medium">—</div>
              </div>
            </div>

            <div className="border rounded p-3 mb-4 text-sm">
              <div className="text-xs text-gray-500 mb-1">ผู้รับเงิน</div>
              <div className="font-medium text-base">
                {data.employee.first_name} {data.employee.last_name}
                {data.employee.nickname && <span className="text-gray-500 text-sm"> ({data.employee.nickname})</span>}
              </div>
              <div className="text-xs text-gray-600 mt-1">
                {data.employee.position} · {data.employee.department}
                <br />
                เลข ปชช.: {data.employee.national_id ?? '—'}
                {data.employee.sso_number && <span> · ปกส.: {data.employee.sso_number}</span>}
              </div>
              {data.employee.address_current && (
                <div className="text-xs text-gray-600 mt-1">
                  ที่อยู่: {data.employee.address_current}
                </div>
              )}
            </div>

            {data.totals && (
              <table className="w-full text-sm border">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left px-3 py-2 border-b">รายการ</th>
                    <th className="text-right px-3 py-2 border-b">จำนวนเงิน (บาท)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td className="px-3 py-1.5">1. เงินเดือน ค่าจ้าง บำเหน็จ บำนาญ</td>
                      <td className="px-3 py-1.5 text-right">{fmt(data.totals.gross_ytd)}</td></tr>
                  <tr><td className="px-3 py-1.5">2. ค่าล่วงเวลา (OT)</td>
                      <td className="px-3 py-1.5 text-right">{fmt(data.totals.ot_pay_ytd)}</td></tr>
                  <tr><td className="px-3 py-1.5">3. รายได้พึงประเมิน (taxable base)</td>
                      <td className="px-3 py-1.5 text-right">{fmt(data.totals.taxable_ytd)}</td></tr>
                  <tr className="border-t-2"><td className="px-3 py-1.5 font-medium">หัก เงินสมทบประกันสังคม</td>
                      <td className="px-3 py-1.5 text-right">{fmt(data.totals.sso_ytd)}</td></tr>
                  <tr><td className="px-3 py-1.5 font-medium">หัก เงินสะสม PF</td>
                      <td className="px-3 py-1.5 text-right">{fmt(data.totals.pf_ytd)}</td></tr>
                  <tr className="border-t-2 bg-gray-50">
                    <td className="px-3 py-2 font-bold">ภาษี ณ ที่จ่ายทั้งหมด</td>
                    <td className="px-3 py-2 text-right font-bold">{fmt(data.totals.tax_ytd)}</td>
                  </tr>
                </tbody>
              </table>
            )}

            <p className="text-[10px] text-gray-400 mt-4 print:hidden">
              ⚠️ ข้อมูลนี้สรุปจาก payroll_runs ที่ถูก lock เท่านั้น · ใช้เป็นเอกสารอ้างอิงภายในก่อน · ก่อนยื่นจริง
              ควรตรวจสอบกับ accountant + เพิ่มเลขผู้เสียภาษี/ข้อมูลเอกสารตามแบบฟอร์ม กรมสรรพากร
            </p>
          </>
        )}
      </div>
    </div>
  );
}
